// Grouping the exception queue by kind, so a screen can offer "close all 222 of
// these" instead of 222 buttons.
//
// The counts must come from the SERVER when we have them. The list endpoint is
// windowed: counting the rows it returned said "500 open exceptions" when the
// tenant had 729, and a bulk action labelled with a page count tells someone
// they are closing 500 things while closing all 729.
import type { AttendanceException } from '@/services/attendanceService';

export interface ExceptionGroup {
  key: string; // 'type/severity'
  type: string;
  severity: string;
  count: number;
}

export const exceptionGroups = (
  items: Pick<AttendanceException, 'type' | 'severity' | 'status'>[],
  counts?: Record<string, number>,
): ExceptionGroup[] => {
  const tally =
    counts && Object.keys(counts).length > 0
      ? counts
      : items.reduce<Record<string, number>>((a, e) => {
          if (e.status !== 'open') return a;
          const k = `${e.type}/${e.severity}`;
          a[k] = (a[k] || 0) + 1;
          return a;
        }, {});

  return Object.entries(tally)
    .map(([key, count]) => {
      const [type, severity = ''] = key.split('/');
      return { key, type, severity, count };
    })
    // A single finding is quicker to resolve on its own row than through a
    // dialog that asks for a reason.
    .filter((g) => g.count > 1)
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
};
